import "react-phone-number-input/style.css"

import type { E164Number } from "libphonenumber-js/types"
import type { FC } from "react"
import type { Control, FieldErrors } from "react-hook-form"
import { type Country, getCountries, getCountryCallingCode } from "react-phone-number-input/input"
// @ts-expect-error - module resolution issue with json import
import en from "react-phone-number-input/locale/en"
import PhoneInput from "react-phone-number-input/react-hook-form"

interface CountrySelectProps {
  value?: string
  onChange: (value: string | undefined) => void
  labels: Record<string, string>
  className?: string
}

const CountrySelect: FC<CountrySelectProps> = ({ value, onChange, labels, className, ...rest }) => {
  const displayValue = value ? `+${getCountryCallingCode(value as Country)}` : ""

  return (
    <div className="relative inline-flex items-center mr-2">
      <div className="flex items-center">
        <span className="text-[13px] font-medium">{displayValue}</span>
        <select
          {...rest}
          className={`absolute inset-0 appearance-none bg-transparent border-none outline-none cursor-pointer opacity-0 ${
            className || ""
          }`}
          onChange={(event) => onChange(event.target.value || undefined)}
          value={value}
        >
          <option value="">{labels.ZZ}</option>
          {getCountries()
            .sort((a, b) => (labels[a] || "").localeCompare(labels[b] || ""))
            .map((country) => (
              <option key={country} value={country}>
                {labels[country]} +{getCountryCallingCode(country)}
              </option>
            ))}
        </select>
        <svg className="ml-1 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path
            clipRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            fillRule="evenodd"
          />
        </svg>
      </div>
    </div>
  )
}

interface PhoneInputFieldProps {
  control: Control<{ phone: E164Number }>
  errors?: FieldErrors<{ phone: string }>
  countryCode: Country | null
  clearErrors: () => void
  className?: string
  inputClassName?: string
}

export const PhoneInputField: FC<PhoneInputFieldProps> = ({
  control,
  errors,
  countryCode,
  clearErrors,
  className,
  inputClassName,
}) => (
  <div
    className={`flex border relative h-12 items-center phoneContainer rounded-[4px] ${
      className || ""
    } ${errors?.phone ? "border-[#FF1C5E] text-error" : "border-[#232734] border-opacity-50 focus-within:border-opacity-100"}`}
  >
    <style>{`
      .PhoneInputInput {
        background-color: transparent !important;
        outline: none !important;
      }
      .PhoneInputInput:focus {
        outline: none !important;
        box-shadow: none !important;
      }
      .PhoneInputCountry {
        margin-right: 8px;
      }
    `}</style>
    <PhoneInput
      className={`ml-4 text-body-lg placeholder-[#232734] placeholder-opacity-50 outline-none ${inputClassName || ""}`}
      control={control}
      countrySelectComponent={CountrySelect}
      countrySelectProps={{
        className: "text-body-lg",
        labels: en,
      }}
      defaultCountry={countryCode || "SE"}
      international={false}
      name="phone"
      onChange={() => {
        if (errors?.phone) {
          clearErrors()
        }
      }}
      placeholder="Phone number"
      withCountryCallingCode={false}
    />
  </div>
)
